import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/blog`)
      .then((res) => setPosts(res.data || []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="blog-page">
      <SEO
        title="Journal — Mental Health Insights & Resources"
        description="Articles from the Seven Pillars of Life clinical team on anxiety, ADHD, autism, relationships, and the slow art of growth."
        path="/blog"
      />
      <PageHeader
        overline="Stories Behind the Scenes"
        title={<>Our <em className="italic text-terracotta">Journal</em></>}
        subtitle="Insights, reflections, and resources from our clinical team — written to support you between sessions."
      />

      <section className="py-16 md:py-24 bg-bone">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <p className="text-ink/60" data-testid="blog-loading">Loading articles...</p>
          ) : posts.length === 0 ? (
            <p className="text-ink/60" data-testid="blog-empty">No posts yet. Check back soon.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((p) => (
                <Link
                  to={`/blog/${p.slug}`}
                  key={p.id}
                  data-testid={`blog-card-${p.slug}`}
                  className="group bg-surface border border-sage/25 rounded-2xl overflow-hidden hover:-translate-y-1 hover:border-terracotta/40 hover:shadow-xl transition-all duration-300 flex flex-col"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={p.cover_image}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-7 flex-1 flex flex-col">
                    <p className="overline mb-3 text-terracotta">{new Date(p.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</p>
                    <h3 className="font-display text-2xl text-forest font-medium leading-tight mb-3 group-hover:text-terracotta transition-colors">
                      {p.title}
                    </h3>
                    <p className="text-ink/70 leading-relaxed text-sm">{p.excerpt}</p>
                    <span className="mt-6 inline-flex items-center gap-2 text-forest font-medium text-sm">
                      Read article <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
